import { useRoutes } from 'react-router-dom';
import router from './routes';
import { ConfigProvider, App as AntdApp, theme } from 'antd';
import './App.css';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  initializeUserFromStorage,
  selectProfileData,
} from '@/store/features/userinfo/userinfoSlice';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SentryErrorBoundary } from './sentry';
import { persistAdvisorLoginBrandContextFromProfile } from './pages/login/brandContext';
import ReportCompletionNotifier from '@/common/components/report-completion-notifier';
import {
  getBrandedActionColors,
  getContrastColor,
  getVisiblePrimaryTextColor,
  isValidCssColor,
} from './util/theme-color';
import SubscriptionEntitlementBoundary from './features/subscription-entitlement/SubscriptionEntitlementBoundary';

const DEFAULT_PRIMARY_COLOR = '#6292d9';

const ThemedApp = () => {
  const { theme: currentTheme, applyDefaultTheme } = useTheme();
  const profileData = useAppSelector(selectProfileData);
  const outlet = useRoutes(router);
  const rawLandingColor = profileData?.landing_color?.trim();
  const landingColor =
    rawLandingColor && isValidCssColor(rawLandingColor) ? rawLandingColor : undefined;
  const primaryColor = landingColor || DEFAULT_PRIMARY_COLOR;

  // Apply the firm's configured default theme on profile load. This only takes
  // effect when the user has not explicitly saved a theme preference, so a
  // personal choice in localStorage always wins.
  const profileDefaultTheme = profileData?.default_theme;
  useEffect(() => {
    applyDefaultTheme(profileDefaultTheme);
  }, [applyDefaultTheme, profileDefaultTheme]);

  // Get CSS variable values from root
  const getCSSVariable = (varName: string) => {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };

  // Determine theme prefix
  const themePrefix = currentTheme === 'dark' ? '--dark' : '--light';
  const pageBackgroundColor = getCSSVariable(`${themePrefix}-bg-color`);
  const componentBackgroundColor = getCSSVariable(`${themePrefix}-component-bg`);
  const sidebarBackgroundColor = getCSSVariable(`${themePrefix}-section-bg`);
  const primaryActionColors = getBrandedActionColors({
    primaryColor,
    surfaceColor: componentBackgroundColor,
    pageBackgroundColor,
  });
  const sidebarPrimaryActionColors = getBrandedActionColors({
    primaryColor,
    surfaceColor: sidebarBackgroundColor,
    pageBackgroundColor,
  });
  const primaryTextAccentColor = getVisiblePrimaryTextColor({
    primaryColor,
    surfaceColor: sidebarBackgroundColor,
    pageBackgroundColor,
  });
  const primaryActionColor = primaryActionColors.backgroundColor;
  const primaryButtonTextColor = primaryActionColors.foregroundColor;
  const primaryActionHoverColor = `color-mix(in srgb, ${primaryActionColor} 88%, #000)`;
  const primaryActionActiveColor = `color-mix(in srgb, ${primaryActionColor} 78%, #000)`;
  const sidebarPrimaryActionColor = sidebarPrimaryActionColors.backgroundColor;
  const menuOnPrimaryColor = sidebarPrimaryActionColors.foregroundColor;
  const sideMenuIconColor = getContrastColor(sidebarBackgroundColor, pageBackgroundColor);

  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary-color', primaryColor);
    document.documentElement.style.setProperty('--ant-color-primary', primaryActionColor);
    document.documentElement.style.setProperty(
      '--primary-button-text-color',
      primaryButtonTextColor,
    );
    document.documentElement.style.setProperty('--menu-on-primary', menuOnPrimaryColor);
    document.documentElement.style.setProperty('--side-menu-icon-color', sideMenuIconColor);
    document.documentElement.style.setProperty('--ant-button-primary-color', primaryActionColor);
    document.documentElement.style.setProperty(
      '--ant-color-primary-hover',
      primaryActionHoverColor,
    );
    document.documentElement.style.setProperty(
      '--ant-color-primary-pressed',
      primaryActionActiveColor,
    );
    document.documentElement.style.setProperty('--theme-color', primaryActionColor);
    document.documentElement.style.setProperty('--theme-text-color', primaryTextAccentColor);
    document.documentElement.style.setProperty('--color-loading-spinner', primaryTextAccentColor);
  }, [
    primaryColor,
    primaryActionColor,
    primaryActionHoverColor,
    primaryActionActiveColor,
    primaryTextAccentColor,
    primaryButtonTextColor,
    menuOnPrimaryColor,
    sideMenuIconColor,
  ]);

  // Mirror the authenticated firm's branding into the login-page localStorage
  // cache so the logout-to-login flow keeps the advisor's firm chrome.
  useEffect(() => {
    persistAdvisorLoginBrandContextFromProfile(profileData, { landingColor });
  }, [profileData, landingColor]);

  return (
    <ConfigProvider
      theme={{
        algorithm: currentTheme === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
        components: {
          Menu: {
            itemBg: getCSSVariable(`${themePrefix}-menu-item-bg`),
            itemSelectedBg: sidebarPrimaryActionColor,
            itemSelectedColor: menuOnPrimaryColor,
            itemHoverBg: sidebarPrimaryActionColor,
            itemHoverColor: menuOnPrimaryColor,
            itemColor: getCSSVariable(`${themePrefix}-menu-item-color`),
            fontSize: 14,
            itemBorderRadius: 21,
            itemMarginInline: 0,
            itemMarginBlock: 0,
            colorSplit: 'rgba(253,253,253,0)',
          },
          Radio: {
            colorText: getCSSVariable(`${themePrefix}-radio-text-color`),
            buttonBg: getCSSVariable(`${themePrefix}-radio-button-bg`),
          },
          Tabs: {
            inkBarColor: primaryTextAccentColor,
            itemActiveColor: primaryTextAccentColor,
            itemHoverColor: primaryTextAccentColor,
            itemSelectedColor: primaryTextAccentColor,
          },
          Modal: {
            titleFontSize: 24,
          },
          Input: {
            controlHeight: 44,
            fontSize: 20,
            borderRadius: 5,
            colorBorder: getCSSVariable(`${themePrefix}-input-border-color`),
          },
          Button: {
            controlHeight: 40,
            fontSize: 16,
            borderRadius: 24,
            defaultColor: getCSSVariable(`${themePrefix}-button-default-color`),
            fontWeight: 600,
            colorPrimary: primaryActionColor,
            defaultHoverBorderColor: primaryActionHoverColor,
            defaultHoverColor: primaryTextAccentColor,
            defaultActiveBorderColor: primaryActionActiveColor,
            defaultActiveColor: primaryTextAccentColor,
            groupBorderColor: primaryActionHoverColor,
            primaryColor: primaryButtonTextColor,
          },
        },
        token: {
          colorPrimary: primaryActionColor,
          colorPrimaryHover: primaryActionHoverColor,
          colorPrimaryActive: primaryActionActiveColor,
          colorLink: primaryTextAccentColor,
          colorLinkHover: primaryTextAccentColor,
          colorLinkActive: primaryActionActiveColor,
          fontFamily: 'Roboto',
        },
      }}
    >
      <AntdApp>
        <ReportCompletionNotifier />
        <SubscriptionEntitlementBoundary>{outlet}</SubscriptionEntitlementBoundary>
      </AntdApp>
    </ConfigProvider>
  );
};

// Error fallback component for Sentry ErrorBoundary
function ErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <h1 style={{ marginBottom: '10px' }}>Something went wrong</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>{errorMessage}</p>
      <button
        onClick={resetError}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          cursor: 'pointer',
          backgroundColor: '#3A7BD5',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
        }}
      >
        Try Again
      </button>
    </div>
  );
}

function App() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const envSuffix = import.meta.env.PROD
      ? ''
      : import.meta.env.MODE === 'dev'
        ? ' - Dev'
        : ' - Staging';
    document.title = 'Wavvest Weave' + envSuffix;

    dispatch(initializeUserFromStorage());
  }, [dispatch]);

  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }: { error: unknown; resetError: () => void }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
      onError={(error) => {
        console.error('App Error Boundary caught:', error);
      }}
    >
      <ThemeProvider>
        <ThemedApp />
      </ThemeProvider>
    </SentryErrorBoundary>
  );
}

export default App;
